import { create } from "zustand";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";

// The client's Projects workspace.
//
// Every number and label here is computed by the API from real project data —
// the store only holds and filters. Nothing is derived client-side, so the
// table, the cards and the AI summary can never disagree with each other.

export type ClientProjectStatus =
  | "Not Started"
  | "In Progress"
  | "Awaiting Client"
  | "On Hold"
  | "Completed"
  | "Cancelled";

export type ProjectHealth =
  | "On Track"
  | "Attention Required"
  | "At Risk"
  | "Delayed"
  | "Awaiting Client"
  | "On Hold"
  | "Completed";

export interface ClientProjectRow {
  id: string;
  reference: string;
  name: string;
  category: string;
  status: ClientProjectStatus;
  health: ProjectHealth;
  healthReason: string;
  /** Whether the health above has already happened or merely might. */
  certainty?: "confirmed" | "potential" | "none";
  currentStage: string;
  nextStage: string | null;
  milestone: string;
  progress: number;
  eta: string | null;
  value: number;
  openActionCount: number;
}

export interface ClientAction {
  id: string;
  type: string;
  title: string;
  detail?: string;
  project: string;
  projectName: string;
  dueDate: string | null;
  priority: "Required" | "Upcoming" | "Blocking";
  status: string;
  stage?: string;
  invoice?: string | null;
  amount?: number;
  currency?: string;
  invoiceNumber?: string;
  /** True only where the project workflow confirms work cannot continue. */
  blocking?: boolean;
}

/** What an invoice means to the client — not our internal billing status. */
export type ClientInvoiceState =
  | "Upcoming"
  | "Due"
  | "Overdue"
  | "Payment Processing"
  | "Partially Paid"
  | "Paid"
  | "Cancelled";

export interface ClientInvoice {
  id: string;
  number: string;
  project: string | null;
  projectName: string;
  currency: string;
  amount: number;
  paid: number;
  outstanding: number;
  status: ClientInvoiceState;
  due: string | null;
  blocking: boolean;
}

/** Where a fact came from, so the UI can open the record behind a sentence. */
export interface FactRef {
  type: "project" | "milestone" | "action" | "invoice" | "deliverable";
  id: string;
  projectId: string | null;
}

export interface ProjectsOverview {
  activeVsClosed: { total: number; active: number; closed: number };
  awaitingActions: { total: number; buckets: Record<string, number> };
  valueAndVolume: {
    currency: string;
    totalValue: number;
    activeValue: number;
    closedValue: number;
    totalProjects: number;
    activeProjects: number;
    closedProjects: number;
  };
  insight: string;
  /** What the insight is reacting to, so severity never has to be read out
      of the prose. */
  attention?: {
    confirmedIssues: number;
    potentialRisks: number;
    overdueActions: number;
    blockingActions: number;
    invoicesBlockingWork: number;
  };
  generatedAt: string;
}

// ── Project requests ────────────────────────────────────────────────────────
// A request is not a project. It is what the client asked for, tracked through
// review; it becomes a project only when the team converts it.

export type ProjectRequestStatus =
  | "Submitted"
  | "Under Review"
  | "More Information Required"
  | "Discussing Requirements"
  | "Approved"
  | "Not Proceeding"
  | "Converted to Project";

export interface RequestAttachment {
  name: string;
  url: string;
  size?: number;
  mimeType?: string;
  r2Key?: string;
}

export interface RequestInfoRound {
  id: string;
  question: string;
  askedAt: string;
  response: string;
  respondedAt: string | null;
  answered: boolean;
  attachments: RequestAttachment[];
}

export interface ProjectRequest {
  id: string;
  requestNumber: string;
  projectTitle: string;
  category: string;
  requirements: string;
  additionalRequirements: string;
  expectedDeliveryDate: string | null;
  status: ProjectRequestStatus;
  isOpen: boolean;
  submittedAt: string;
  updatedAt: string;
  documents: RequestAttachment[];
  infoRequests: RequestInfoRound[];
  history: { status: string; at: string }[];
  project: string | null;
  convertedAt: string | null;
}

export interface NewProjectRequest {
  projectTitle: string;
  category: string;
  requirements: string;
  additionalRequirements?: string;
  expectedDeliveryDate?: string;
  documents?: RequestAttachment[];
}

export interface ProjectFilters {
  quick: "all" | "active" | "awaiting" | "closed";
  search: string;
  category: string;
  status: string;
  stage: string;
  health: string;
  action: string;
  from: string;
  to: string;
  page: number;
  limit: number;
}

export const EMPTY_FILTERS: ProjectFilters = {
  quick: "all",
  search: "",
  category: "",
  status: "",
  stage: "",
  health: "",
  action: "",
  from: "",
  to: "",
  page: 1,
  limit: 10,
};

interface State {
  overview: ProjectsOverview | null;
  isLoadingOverview: boolean;

  projects: ClientProjectRow[];
  total: number;
  counts: { all: number; active: number; awaiting: number; closed: number };
  options: {
    categories: string[];
    stages: string[];
    healths: string[];
    statuses: string[];
    actions: string[];
  };
  isLoadingProjects: boolean;

  filters: ProjectFilters;
  setFilters: (patch: Partial<ProjectFilters>) => void;
  resetFilters: () => void;

  fetchOverview: () => Promise<void>;
  fetchProjects: () => Promise<void>;
  fetchProject: (id: string) => Promise<any | null>;
  fetchLifecycle: (id: string) => Promise<any | null>;
  fetchAiSummary: () => Promise<any | null>;
  completeAction: (actionId: string, body?: { response?: string }) => Promise<boolean>;

  // Project requests
  requests: ProjectRequest[];
  requestsAwaitingYou: number;
  isLoadingRequests: boolean;
  requestCategories: string[];

  fetchRequests: () => Promise<void>;
  fetchRequestOptions: () => Promise<void>;
  uploadRequestFiles: (files: File[]) => Promise<RequestAttachment[]>;
  submitProjectRequest: (body: NewProjectRequest) => Promise<boolean>;
  respondToRequest: (id: string, response: string) => Promise<boolean>;
}

const EMPTY_COUNTS = { all: 0, active: 0, awaiting: 0, closed: 0 };
const EMPTY_OPTIONS = { categories: [], stages: [], healths: [], statuses: [], actions: [] };

export const useClientProjectsStore = create<State>((set, get) => ({
  overview: null,
  isLoadingOverview: false,

  projects: [],
  total: 0,
  counts: EMPTY_COUNTS,
  options: EMPTY_OPTIONS,
  isLoadingProjects: false,

  filters: { ...EMPTY_FILTERS },

  requests: [],
  requestsAwaitingYou: 0,
  isLoadingRequests: false,
  requestCategories: [],

  // Any filter change returns to the first page — staying on page 4 of a
  // three-page result is the classic way to show an empty table by accident.
  setFilters: (patch) =>
    set({ filters: { ...get().filters, ...patch, page: patch.page ?? 1 } }),

  resetFilters: () => set({ filters: { ...EMPTY_FILTERS } }),

  fetchOverview: async () => {
    set({ isLoadingOverview: true });
    try {
      const res = await axiosInstance.get<ProjectsOverview>("/client-portal/projects/overview");
      set({ overview: res.data });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not load your project overview.");
    } finally {
      set({ isLoadingOverview: false });
    }
  },

  fetchProjects: async () => {
    set({ isLoadingProjects: true });
    try {
      const f = get().filters;
      const params: Record<string, string> = {};
      Object.entries(f).forEach(([k, v]) => {
        if (v !== "" && v !== undefined && v !== null) params[k] = String(v);
      });
      const res = await axiosInstance.get("/client-portal/projects", { params });
      set({
        projects: res.data.projects || [],
        total: res.data.total || 0,
        counts: res.data.counts || EMPTY_COUNTS,
        options: res.data.options || EMPTY_OPTIONS,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not load your projects.");
    } finally {
      set({ isLoadingProjects: false });
    }
  },

  fetchProject: async (id) => {
    try {
      const res = await axiosInstance.get(`/client-portal/projects/${id}`);
      return res.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not load this project.");
      return null;
    }
  },

  fetchLifecycle: async (id) => {
    try {
      const res = await axiosInstance.get(`/client-portal/projects/${id}/lifecycle`);
      return res.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not load the project lifecycle.");
      return null;
    }
  },

  fetchAiSummary: async () => {
    try {
      const res = await axiosInstance.get("/client-portal/projects/ai-summary");
      return res.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not build your project summary.");
      return null;
    }
  },

  completeAction: async (actionId, body = {}) => {
    try {
      const res = await axiosInstance.post(
        `/client-portal/projects/actions/${actionId}/complete`,
        body
      );
      toast.success(res.data?.message || "Recorded.");
      await Promise.all([get().fetchOverview(), get().fetchProjects()]);
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not record your response.");
      return false;
    }
  },

  // ── Project requests ──────────────────────────────────────────────────────

  fetchRequests: async () => {
    set({ isLoadingRequests: true });
    try {
      const res = await axiosInstance.get("/client-portal/project-requests");
      set({
        requests: res.data.requests || [],
        requestsAwaitingYou: res.data.awaitingYou || 0,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not load your requests.");
    } finally {
      set({ isLoadingRequests: false });
    }
  },

  // The category list comes from the server rather than being hardcoded here,
  // because it is the same list the request is validated against.
  fetchRequestOptions: async () => {
    try {
      const res = await axiosInstance.get("/client-portal/project-requests/options");
      set({ requestCategories: res.data.categories || [] });
    } catch {
      // A missing list is not worth a toast — the form says so itself.
      set({ requestCategories: [] });
    }
  },

  uploadRequestFiles: async (files) => {
    if (!files.length) return [];
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const res = await axiosInstance.post("/client-portal/project-requests/attachments", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.files || [];
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not upload your files.");
      return [];
    }
  },

  submitProjectRequest: async (body) => {
    try {
      const res = await axiosInstance.post("/client-portal/project-requests", body);
      toast.success(res.data?.message || "Your request has been submitted.");
      await get().fetchRequests();
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not submit your request.");
      return false;
    }
  },

  respondToRequest: async (id, response) => {
    try {
      const res = await axiosInstance.post(`/client-portal/project-requests/${id}/respond`, {
        response,
      });
      toast.success(res.data?.message || "Sent to the team.");
      await get().fetchRequests();
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not send your response.");
      return false;
    }
  },
}));
